import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './store';
import { checkAuthRequest } from './store/actions/authActions';
import { LoginPage } from './pages/auth/LoginPage';
import { SignupPage } from './pages/auth/SignupPage';
import { EmailVerificationPage } from './pages/auth/EmailVerificationPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectCreatePage } from './pages/ProjectCreatePage';
import { ProjectGeneratingPage } from './pages/ProjectGeneratingPage';
import { ProjectDetailsPage } from './pages/ProjectDetailsPage';
import { UserStoriesPage } from './pages/UserStoriesPage';
import { UserStoryDetailsPage } from './pages/UserStoryDetailsPage';
import { TestCasesPage } from './pages/TestCasesPage';
import { TestCaseDetailsPage } from './pages/TestCaseDetailsPage';
import { EditTestCasePage } from './pages/EditTestCasePage';
import { LandingPage } from './pages/LandingPage';
import { CustomPricingPage } from './pages/CustomPricingPage';
import { SubscriptionPricingPage } from './pages/SubscriptionPricingPage';
import { PaymentPage } from './pages/PaymentPage';
import { AdminPanelPage } from './pages/AdminPanelPage';
import { ProfilePage } from './pages/ProfilePage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { AiStrategyPage } from './pages/AiStrategyPage';
import { DraftsPage } from './pages/DraftsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import { Layout } from './components/Layout';

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, isLoading, token, user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (token && !isAuthenticated) {
      dispatch(checkAuthRequest());
    }
  }, [dispatch, token, isAuthenticated]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-bg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/subscription-pricing" element={<SubscriptionPricingPage />} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/verify-email/:token" element={<EmailVerificationPage />} />
        <Route path="/custom-pricing" element={<CustomPricingPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/admin/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to={window.location.pathname.startsWith('/admin') ? "/admin/login" : "/login"} replace />} />
      </Routes>
    );
  }

  // Check if user has pending payment status and redirect to payment page
  const hasPendingPayment = user?.subscribed === 'P';

  return (
    <Routes>
      <Route path="/pricing" element={<LandingPage />} />
      <Route path="/subscription-pricing" element={<SubscriptionPricingPage />} />
      <Route path="/payment" element={<PaymentPage />} />
      <Route path="/custom-pricing" element={<CustomPricingPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/verify-email/:token" element={<EmailVerificationPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/admin/login" element={<LoginPage />} />
      <Route
        path="*"
        element={
          hasPendingPayment ? (
            <Navigate to="/subscription-pricing" replace />
          ) : (
            <Layout>
              <Routes>
                <Route path="/" element={<Navigate to={user?.role === 'admin' ? "/admin" : "/dashboard"} replace />} />
                <Route path="/dashboard" element={user?.role === 'admin' ? <Navigate to="/admin" replace /> : <DashboardPage />} />
                <Route path="/admin" element={<AdminPanelPage />} />
                <Route path="/projects/create" element={user?.role === 'admin' ? <Navigate to="/admin" replace /> : <ProjectCreatePage />} />
                <Route path="/projects/:id" element={user?.role === 'admin' ? <Navigate to="/admin" replace /> : <ProjectDetailsPage />} />
                <Route path="/projects/:id/generating" element={user?.role === 'admin' ? <Navigate to="/admin" replace /> : <ProjectGeneratingPage />} />
                <Route path="/requirements/:requirementId/user-stories" element={user?.role === 'admin' ? <Navigate to="/admin" replace /> : <UserStoriesPage />} />
                <Route path="/user-stories/:id" element={user?.role === 'admin' ? <Navigate to="/admin" replace /> : <UserStoryDetailsPage />} />
                <Route path="/user-stories/:id/test-cases" element={user?.role === 'admin' ? <Navigate to="/admin" replace /> : <TestCasesPage />} />
                <Route path="/test-cases/:id" element={user?.role === 'admin' ? <Navigate to="/admin" replace /> : <TestCaseDetailsPage />} />
                <Route path="/test-cases/:id/edit" element={user?.role === 'admin' ? <Navigate to="/admin" replace /> : <EditTestCasePage />} />
                <Route path="/ai-strategy" element={user?.role === 'admin' ? <Navigate to="/admin" replace /> : <AiStrategyPage />} />
                <Route path="/drafts" element={user?.role === 'admin' ? <Navigate to="/admin" replace /> : <DraftsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/integrations" element={<IntegrationsPage />} />
                <Route path="*" element={<Navigate to={user?.role === 'admin' ? "/admin" : "/dashboard"} replace />} />
              </Routes>
            </Layout>
          )
        }
      />
    </Routes>
  );
}

export default App;
